import clsx from 'clsx';
import { ArrowRightLeft, Grid3x3, Mic, MicOff, Pause, Phone, PhoneOff, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSoftphone } from '../app/softphone';
import { formatDuration } from '../lib/format';
import { ExtensionPicker } from './ExtensionPicker';
import { Button } from './ui';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

/** Controles da ligação no navegador: atender/recusar, desligar, mudo, espera, teclado e transferir. */
export function SoftphoneCallBar() {
  const { call, engine, extension } = useSoftphone();
  const [now, setNow] = useState(Date.now());
  const [panel, setPanel] = useState<null | 'keys' | 'transfer'>(null);
  const [target, setTarget] = useState('');

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => setPanel(null), [call?.id]);
  if (!call) return null;

  const ringing = call.phase === 'ringing';
  const seconds = Math.floor((now - (call.answeredAt ?? call.startedAt)) / 1000);
  const label = call.phase === 'ringing' ? 'Ligação chegando' : call.phase === 'calling' ? 'Chamando…' : call.held ? 'Em espera' : 'Em ligação';

  return (
    <div className={clsx('rounded-lg border bg-panel p-3 shadow-lg', ringing ? 'border-accent ring-2 ring-accent/30' : 'border-ok/50')}>
      <div className="flex items-center justify-between gap-2 text-xs text-fg-soft">
        <span>{label} · telefone do navegador</span>
        <span className="font-mono">{formatDuration(Math.max(0, seconds))}</span>
      </div>
      <div className="truncate font-semibold">{call.remoteName ? `${call.remoteName} (${call.remote})` : call.remote}</div>

      {ringing ? (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Button variant="success" onClick={() => engine.answer()}>
            <Phone className="size-4" /> Atender
          </Button>
          <Button variant="danger" onClick={() => engine.hangup()}>
            <PhoneOff className="size-4" /> Recusar
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Button size="sm" variant="danger" onClick={() => engine.hangup()}>
              <PhoneOff className="size-3.5" /> Desligar
            </Button>
            {call.phase === 'active' && (
              <>
                <Button size="sm" variant={call.muted ? 'primary' : 'secondary'} onClick={() => engine.toggleMute()} title={call.muted ? 'Ligar o microfone' : 'Silenciar o microfone'}>
                  {call.muted ? <MicOff className="size-3.5" /> : <Mic className="size-3.5" />} {call.muted ? 'Sem som' : 'Mudo'}
                </Button>
                <Button size="sm" variant={call.held ? 'primary' : 'secondary'} onClick={() => engine.toggleHold()}>
                  {call.held ? <Play className="size-3.5" /> : <Pause className="size-3.5" />} {call.held ? 'Retomar' : 'Espera'}
                </Button>
                <Button size="sm" variant={panel === 'keys' ? 'primary' : 'secondary'} onClick={() => setPanel(panel === 'keys' ? null : 'keys')} title="Teclado (URA)">
                  <Grid3x3 className="size-3.5" />
                </Button>
                <Button size="sm" variant={panel === 'transfer' ? 'primary' : 'secondary'} onClick={() => setPanel(panel === 'transfer' ? null : 'transfer')} title="Transferir">
                  <ArrowRightLeft className="size-3.5" />
                </Button>
              </>
            )}
          </div>
          {panel === 'keys' && (
            <div className="mt-2 grid grid-cols-3 gap-1">
              {KEYS.map((k) => (
                <button key={k} className="rounded-md border border-border py-1.5 font-mono hover:bg-muted" onClick={() => engine.dtmf(k)}>
                  {k}
                </button>
              ))}
            </div>
          )}
          {panel === 'transfer' && (
            <form
              className="mt-2 flex gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (target.trim()) engine.transfer(target);
                setTarget('');
                setPanel(null);
              }}
            >
              <ExtensionPicker value={target} onChange={setTarget} placeholder="Nome, ramal, fila ou número" excludeExtension={extension} autoFocus />
              <Button type="submit" size="sm" variant="primary" disabled={!target.trim()}>
                Transferir
              </Button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
